import { useGetIdentity, useInvalidateAuthStore, useLogout } from "@refinedev/core";
import { Alert, Button, Flex, Form, Input, List, QRCode, Steps, Typography } from "antd";
import { useState } from "react";
import { Navigate, useNavigate } from "react-router";

import { authClient, authErrorMessage } from "../auth-client";
import { COPY_TOOLTIPS } from "../labels";
import { clearMe, type Me } from "../session";
import { AuthCard } from "./auth-card";

type Enrollment = { totpURI: string; backupCodes: string[] };

/**
 * Required before any office screen (docs/ARCHITECTURE.md → Staff Authentication): confirm the
 * password, scan the QR code, save the backup codes, then confirm with a code from the app.
 */
export function SetupTwoFactorPage() {
  const { data: me } = useGetIdentity<Me>();
  const invalidateAuth = useInvalidateAuthStore();
  const { mutate: logout } = useLogout();
  const navigate = useNavigate();
  const [enrollment, setEnrollment] = useState<Enrollment | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (me?.twoFactorEnabled) return <Navigate to="/" replace />;

  async function start({ password }: { password: string }) {
    setBusy(true);
    setError(null);
    const { data, error: failure } = await authClient.twoFactor.enable({ password });
    setBusy(false);
    if (failure || data?.method !== "totp") return setError(authErrorMessage(failure ?? {}));
    setEnrollment({ totpURI: data.totpURI, backupCodes: data.backupCodes });
  }

  async function confirm({ code }: { code: string }) {
    setBusy(true);
    setError(null);
    const { error: failure } = await authClient.twoFactor.verifyTotp({ code });
    setBusy(false);
    if (failure) return setError(authErrorMessage(failure));
    clearMe();
    await invalidateAuth();
    navigate("/", { replace: true });
  }

  const secret = enrollment ? new URL(enrollment.totpURI).searchParams.get("secret") : null;

  return (
    <AuthCard title="İki adımlı doğrulama">
      <Steps
        size="small"
        current={enrollment ? 1 : 0}
        items={[{ title: "Şifre" }, { title: "Uygulama" }]}
        style={{ marginBottom: 24 }}
      />
      {error ? <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} /> : null}
      {enrollment ? (
        <>
          <Typography.Paragraph>
            Google Authenticator, Microsoft Authenticator veya benzeri bir uygulamayla QR kodu okut.
            Okutamazsan anahtarı elle gir.
          </Typography.Paragraph>
          <Flex justify="center" style={{ marginBottom: 12 }}>
            <QRCode value={enrollment.totpURI} />
          </Flex>
          <Typography.Paragraph
            copyable={{ text: secret ?? "", tooltips: COPY_TOOLTIPS }}
            code
            data-testid="totp-secret"
          >
            {secret}
          </Typography.Paragraph>
          <Typography.Paragraph strong>
            Yedek kodlar: telefonunu kaybedersen bunlarla giriş yaparsın. Güvenli bir yere kaydet;
            bir daha gösterilmez.
          </Typography.Paragraph>
          <List
            size="small"
            bordered
            dataSource={enrollment.backupCodes}
            renderItem={(code) => (
              <List.Item>
                <Typography.Text code>{code}</Typography.Text>
              </List.Item>
            )}
            style={{ marginBottom: 16 }}
          />
          <Typography.Paragraph
            copyable={{ text: enrollment.backupCodes.join("\n"), tooltips: COPY_TOOLTIPS }}
          >
            Yedek kodları kopyala
          </Typography.Paragraph>
          <Form<{ code: string }> layout="vertical" requiredMark={false} onFinish={confirm}>
            <Form.Item
              label="Uygulamadaki kod"
              name="code"
              rules={[{ required: true, pattern: /^\d{6}$/, message: "6 haneli kodu gir." }]}
            >
              <Input inputMode="numeric" autoComplete="one-time-code" maxLength={6} />
            </Form.Item>
            <Button type="primary" htmlType="submit" block loading={busy}>
              Doğrula ve devam et
            </Button>
          </Form>
        </>
      ) : (
        <Form<{ password: string }> layout="vertical" requiredMark={false} onFinish={start}>
          <Typography.Paragraph type="secondary">
            Office'e her girişte şifrenin yanında authenticator uygulamasındaki kod da istenir.
            Başlamak için şifreni gir.
          </Typography.Paragraph>
          <Form.Item
            label="Şifre"
            name="password"
            rules={[{ required: true, message: "Şifreni gir." }]}
          >
            <Input.Password autoComplete="current-password" autoFocus />
          </Form.Item>
          <Button type="primary" htmlType="submit" block loading={busy}>
            Devam
          </Button>
        </Form>
      )}
      <Button type="link" block style={{ marginTop: 8 }} onClick={() => logout()}>
        Çıkış yap
      </Button>
    </AuthCard>
  );
}
